import { useNavigate } from "react-router-dom";
import { useGetUserMe } from "../../features/user";
import {
  useConfirmTransaction,
  useDeleteTransaction,
  useGetTransaction,
} from "../../features/transaction";
import NumberFormat from "../../utils/NumberFormat";
import Badge from "../../utils/Badge";
import { FiEye, FiTrash, FiX } from "react-icons/fi";
import { safeDateFormat } from "../../utils/saveDateFormat";
import ModalConfirmationDelete from "../../components/ModalConfirmationDelete";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AiFillWarning, AiOutlineCheckCircle } from "react-icons/ai";
import { useDownloadTransactionExcel } from "../../features/transaction/hooks/useGetTransactionDownload";
import { toast } from "react-toastify";

const OrderListPage = () => {
  const router = useNavigate();
  const { userMe } = useGetUserMe();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState<string>(null);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const { transaction, isLoading } = useGetTransaction({ startDate, endDate });
  const { Delete } = useDeleteTransaction();
  const { ConfirmTransaction } = useConfirmTransaction();
  const { downloadExcel } = useDownloadTransactionExcel();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenConfirmTransaction, setIsModalOpenConfirmationTransaction] =
    useState(false);
  const [id, setId] = useState("");

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsModalOpenConfirmationTransaction(false);
    setId("");
  };

  const handleConfirmDelete = () => {
    Delete(id, {
      onSuccess: () => handleCloseModal(),
    });
  };

  const handleConfirmDTransaction = () => {
    ConfirmTransaction(id, {
      onSuccess: () => handleCloseModal(),
    });
  };

  return (
    <>
      <div className="p-6">
        {isLoading ? (
          <div>
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex justify-between border-b pb-2 mb-4 hover:bg-gray-100 p-4 rounded-lg shadow-sm"
              >
                <div>
                  <Skeleton width={100} />
                  <Skeleton width={200} />
                </div>
                <div className="text-right">
                  <Skeleton width={100} />
                </div>
              </div>
            ))}
          </div>
        ) : userMe?.role === "USER" ? (
          <div className="space-y-4">
            {transaction?.content?.map((order, index) => (
              <div
                key={index}
                className="flex justify-between border-b pb-2 mb-4 cursor-pointer hover:bg-gray-100 p-4 rounded-lg shadow-sm"
                onClick={() => router(`/order-list/detail/${order?.id}`)}
              >
                <div>
                  <span className="text-[10px] text-gray-500">
                    {safeDateFormat(order?.createdAt, true)}
                  </span>
                  <h6 className="text-sm text-blue-600">
                    {order?.product?.name}{" "}
                    <span className="text-xs">
                      (<NumberFormat value={order?.price} prefix="Rp. " />)
                    </span>
                  </h6>
                </div>
                <div className="text-right">
                  <Badge status={order?.status as any} />
                  <div className="text-sm text-gray-600 font-semibold mt-1.5">
                    <NumberFormat value={order?.totalPrice} prefix="Rp. " />
                  </div>
                </div>
              </div>
            ))}
            {transaction?.content?.length === 0 && (
              <div className="text-center py-10">
                <AiFillWarning className="mx-auto" size={40} color="gray" />
                <h2 className="text-gray-500 text-lg font-semibold">
                  Tidak ada transaksi
                </h2>
                <p className="text-gray-400 text-sm">
                  Pesanan kamu akan muncul di sini setelah melakukan transaksi.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex justify-between">
              <h1 className="mt-2 mb-5 font-semibold text-xl text-gray-900">
                Transaction
              </h1>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setFilterModalOpen(true)}
                  className="bg-gray-700 text-white text-sm cursor-pointer px-4 py-2 rounded hover:bg-gray-800"
                >
                  Filter
                </button>

                <button
                  onClick={() => {
                    downloadExcel({ startDate, endDate });
                  }}
                  className="bg-green-600 text-white text-sm cursor-pointer px-4 py-2 rounded hover:bg-green-700"
                >
                  Download Excel
                </button>
              </div>
            </div>
            {startDate && endDate && (
              <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded shadow-sm border flex justify-between border-blue-200">
                <p className="text-sm">
                  Menampilkan transaksi dari{" "}
                  <strong>{safeDateFormat(startDate, true)}</strong> hingga{" "}
                  <strong>{safeDateFormat(endDate, true)}</strong>
                </p>
                <FiX
                  className="cursor-pointer"
                  onClick={() => {
                    setEndDate(null);
                    setStartDate(null);
                  }}
                />
              </div>
            )}
            <table className="min-w-full bg-white rounded-lg shadow-md">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Discount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Total Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-medium">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transaction?.content?.map((order, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {safeDateFormat(order?.createdAt, true)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order?.createdBy?.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order?.product?.code} - {order?.product?.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <NumberFormat value={order?.discount} prefix="Rp. " />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <NumberFormat value={order?.price} prefix="Rp. " />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <NumberFormat value={order?.totalPrice} prefix="Rp. " />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order?.createdBy?.tenant?.name ?? "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order?.phone}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order?.status}
                    </td>
                    <td className="px-6 py-4 cursor-pointer text-sm text-gray-700 flex gap-3 items-center">
                      <div
                        onClick={() =>
                          router(`/order-list/detail/${order?.id}`)
                        }
                      >
                        <FiEye />
                      </div>
                      <div
                        onClick={() => {
                          setIsModalOpen(true);
                          setId(order?.id);
                        }}
                      >
                        <FiTrash />
                      </div>
                      {order.status === "NEW" && userMe?.role === "CS" && (
                        <div
                          onClick={() => {
                            setIsModalOpenConfirmationTransaction(true);
                            setId(order?.id);
                          }}
                        >
                          <AiOutlineCheckCircle />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Filter */}
      {filterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-md">
            <h2 className="text-lg font-semibold mb-4">Filter </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Start Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2"
                  value={tempStartDate}
                  onChange={(e) => setTempStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Finish Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2"
                  value={tempEndDate}
                  onChange={(e) => setTempEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6 gap-3">
              <button
                className="px-4 py-2 rounded border text-gray-700"
                onClick={() => setFilterModalOpen(false)}
              >
                Batal
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-sm text-white rounded hover:bg-blue-700"
                onClick={() => {
                  if (tempEndDate && tempStartDate) {
                    setFilterModalOpen(false);
                    setStartDate(tempStartDate);
                    setEndDate(tempEndDate);
                  } else {
                    toast.warning("Harap Lengkapi Form");
                  }
                }}
              >
                Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Delete & Confirm Transaction */}
      <ModalConfirmationDelete
        isOpen={isModalOpen}
        title="Apakah Anda yakin akan Delete Transaction"
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
      />
      <ModalConfirmationDelete
        isOpen={isModalOpenConfirmTransaction}
        title="Apakah Anda yakin akan Konfirmasi Transaction"
        onClose={handleCloseModal}
        onConfirm={handleConfirmDTransaction}
      />
    </>
  );
};

export default OrderListPage;
